import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  message,
  Typography,
  Button,
  Select,
  DatePicker,
  Space,
  Tag,
  Alert,
  Tabs,
  Divider,
} from 'antd';
import {
  ReloadOutlined,
  WarningOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { apiClient } from '../../../config/apiClient';
import { usePermissions } from '../../../context/permissionsContext';
import MainLayout from '../../layout/layout';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Parsear timestamp de backend (hora local Argentina)
const formatFechaConHora = (timestamp) => {
  if (!timestamp) return '-';
  if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
    return dayjs(timestamp.replace(' ', 'T')).format('DD/MM/YYYY HH:mm');
  }
  return dayjs(timestamp).format('DD/MM/YYYY HH:mm');
};

const { Title } = Typography;
const { RangePicker } = DatePicker;

const COLORS = ['#52c41a', '#1890ff', '#faad14', '#eb2f96', '#13c2c2', '#722ed1', '#f5222d', '#2f54eb'];

const DashboardGestion = () => {
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [resumenCajaBar, setResumenCajaBar] = useState(null);
  const [loadingResumenCaja, setLoadingResumenCaja] = useState(false);
  const [resumenFinanciero, setResumenFinanciero] = useState(null);
  const [loadingFinanciero, setLoadingFinanciero] = useState(false);
  const [usuarioFiltroId, setUsuarioFiltroId] = useState(null);
  const [listaUsuarios, setListaUsuarios] = useState([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [periodo, setPeriodo] = useState('hoy');
  const [fechaDesde, setFechaDesde] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(null);

  const getFechasPeriodo = () => {
    const hoy = dayjs();
    let desde;
    let hasta;

    switch (periodo) {
      case 'hoy':
        desde = hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'ayer':
        desde = hasta = hoy.subtract(1, 'day').format('YYYY-MM-DD');
        break;
      case 'ultimos7':
        desde = hoy.subtract(7, 'day').format('YYYY-MM-DD');
        hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'ultimos30':
        desde = hoy.subtract(30, 'day').format('YYYY-MM-DD');
        hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'mesActual':
        desde = hoy.startOf('month').format('YYYY-MM-DD');
        hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'mesAnterior':
        desde = hoy.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        hasta = hoy.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      case 'anoActual':
        desde = hoy.startOf('year').format('YYYY-MM-DD');
        hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'personalizado':
        desde = fechaDesde ? fechaDesde.format('YYYY-MM-DD') : null;
        hasta = fechaHasta ? fechaHasta.format('YYYY-MM-DD') : null;
        break;
      default:
        desde = hasta = hoy.format('YYYY-MM-DD');
    }

    return { desde, hasta };
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const { desde, hasta } = getFechasPeriodo();

      const params = new URLSearchParams();
      if (desde) params.append('fecha_desde', desde);
      if (hasta) params.append('fecha_hasta', hasta);
      if (usuarioFiltroId != null) {
        params.append('usuario_id', String(usuarioFiltroId));
      }

      const response = await apiClient.get(`/gestion/dashboard?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los datos del dashboard');
      }
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      message.error('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadResumenCajaBar = async () => {
    const { desde, hasta } = getFechasPeriodo();
    if (!desde || !hasta) {
      setResumenCajaBar(null);
      return;
    }
    setLoadingResumenCaja(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_desde', desde);
      params.append('fecha_hasta', hasta);
      if (usuarioFiltroId != null) {
        params.append('usuario_id', String(usuarioFiltroId));
      }
      const response = await apiClient.get(`/gestion/dashboard/resumen-caja?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setResumenCajaBar(data);
      } else {
        setResumenCajaBar(null);
      }
    } catch (e) {
      console.error('Error al cargar resumen caja:', e);
      setResumenCajaBar(null);
    } finally {
      setLoadingResumenCaja(false);
    }
  };

  const loadResumenFinanciero = async () => {
    const { desde, hasta } = getFechasPeriodo();
    if (!desde || !hasta) {
      setResumenFinanciero(null);
      return;
    }
    setLoadingFinanciero(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_desde', desde);
      params.append('fecha_hasta', hasta);
      if (usuarioFiltroId != null) {
        params.append('usuario_id', String(usuarioFiltroId));
      }
      const response = await apiClient.get(
        `/gestion/dashboard/resumen-financiero?${params.toString()}`
      );
      if (response.ok) {
        setResumenFinanciero(await response.json());
      } else {
        setResumenFinanciero(null);
      }
    } catch (e) {
      console.error('Error al cargar resumen financiero:', e);
      setResumenFinanciero(null);
    } finally {
      setLoadingFinanciero(false);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [periodo, fechaDesde, fechaHasta, usuarioFiltroId]);

  useEffect(() => {
    if (!hasPermission('usuarios', 'ver')) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get('/gestion/usuarios?incluir_inactivos=false');
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const staff = (Array.isArray(data) ? data : []).filter((u) => u.usu_tipousuario === 1);
        if (!cancelled) setListaUsuarios(staff);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasPermission]);

  useEffect(() => {
    loadResumenCajaBar();
    loadResumenFinanciero();
  }, [periodo, fechaDesde, fechaHasta, usuarioFiltroId]);

  const formatMoneda = (n) =>
    `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const formatMonedaDecimal = (n) =>
    `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatFecha = (dateString) => (dateString ? dayjs(dateString).format('DD/MM/YYYY') : '-');
  const formatFechaCorta = (dateString) => (dateString ? dayjs(dateString).format('DD/MM') : '-');

  // Datos para gráfico de ingresos vs egresos
  const ingresosVsEgresosData = useMemo(() => {
    if (!dashboardData?.graficos?.ingresosVsEgresos) return [];
    return dashboardData.graficos.ingresosVsEgresos.map(item => ({
      fecha: formatFechaCorta(item.fecha),
      ingresos: item.ingresos,
      egresos: item.egresos,
      ganancia: item.ganancia,
    }));
  }, [dashboardData?.graficos?.ingresosVsEgresos]);

  // Datos para gráfico de ventas por categoría
  const ventasPorCategoriaData = useMemo(() => {
    if (!dashboardData?.graficos?.ventasPorCategoria) return [];
    return dashboardData.graficos.ventasPorCategoria.map(item => ({
      categoria: item.categoria,
      monto: item.monto_total,
      cantidad: item.cantidad_vendida,
    }));
  }, [dashboardData?.graficos?.ventasPorCategoria]);

  // Datos para gráfico de ingresos por forma de pago
  const ingresosPorFormaPagoData = useMemo(() => {
    if (!dashboardData?.graficos?.ingresosPorFormaPago) return [];
    return dashboardData.graficos.ingresosPorFormaPago.map((item, index) => ({
      name: item.forma_pago,
      value: item.monto_total,
      cantidad: item.cantidad_ventas,
      fill: COLORS[index % COLORS.length],
    }));
  }, [dashboardData?.graficos?.ingresosPorFormaPago]);

  // Datos para gráfico de gastos por artículo
  const gastosPorPeriodoData = useMemo(() => {
    if (!dashboardData?.graficos?.gastosPorPeriodo) return [];
    return dashboardData.graficos.gastosPorPeriodo.map((item) => ({
      nombre: item.articulo_nombre,
      total: item.total,
      cantidad: item.cantidad,
    }));
  }, [dashboardData?.graficos?.gastosPorPeriodo]);

  // Columnas para tabla de top productos
  const columnsTopProductos = [
    {
      title: 'Producto',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Cantidad Vendida',
      dataIndex: 'cantidad_vendida',
      key: 'cantidad_vendida',
      align: 'center',
    },
    {
      title: 'Monto Total',
      dataIndex: 'monto_total',
      key: 'monto_total',
      align: 'right',
      render: (monto) => formatMoneda(monto),
    },
  ];

  // Columnas para tabla de top proveedores
  const columnsTopProveedores = [
    {
      title: 'Proveedor',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Cantidad Compras',
      dataIndex: 'cantidad_compras',
      key: 'cantidad_compras',
      align: 'center',
    },
    {
      title: 'Monto Total',
      dataIndex: 'monto_total',
      key: 'monto_total',
      align: 'right',
      render: (monto) => formatMoneda(monto),
    },
  ];

  // Columnas para tabla de stock bajo
  const columnsStockBajo = [
    {
      title: 'Artículo',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Stock Actual',
      dataIndex: 'stock_actual',
      key: 'stock_actual',
      align: 'center',
      render: (stock) => <Tag color="red">{stock}</Tag>,
    },
    {
      title: 'Stock Mínimo',
      dataIndex: 'stock_minimo',
      key: 'stock_minimo',
      align: 'center',
    },
    {
      title: 'Diferencia',
      dataIndex: 'diferencia',
      key: 'diferencia',
      align: 'center',
      render: (diff) => <Tag color="orange">-{diff}</Tag>,
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria',
      key: 'categoria',
    },
  ];

  // Columnas para últimas ventas
  const columnsUltimasVentas = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_creacion',
      key: 'fecha',
      render: (fechaCreacion, record) => fechaCreacion ? formatFechaConHora(fechaCreacion) : formatFecha(record.fecha),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (total) => formatMoneda(total),
    },
    {
      title: 'Método Pago',
      dataIndex: 'metodo_pago',
      key: 'metodo_pago',
    },
    {
      title: 'Items',
      dataIndex: 'cantidad_items',
      key: 'cantidad_items',
      align: 'center',
    },
  ];

  // Columnas para últimas compras
  const columnsUltimasCompras = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_creacion',
      key: 'fecha',
      render: (fechaCreacion, record) => fechaCreacion ? formatFechaConHora(fechaCreacion) : formatFecha(record.fecha),
    },
    {
      title: 'Proveedor',
      dataIndex: 'proveedor_nombre',
      key: 'proveedor_nombre',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (total) => total ? formatMoneda(total) : '-',
    },
    {
      title: 'Items',
      dataIndex: 'cantidad_items',
      key: 'cantidad_items',
      align: 'center',
    },
  ];

  const columnsMovimiento = (color) => [
    {
      title: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      render: (concepto) => <span style={{ color: '#e8e8e8' }}>{concepto}</span>,
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      align: 'right',
      width: 160,
      render: (monto) => (
        <span style={{ color, fontWeight: 500 }}>{formatMoneda(monto)}</span>
      ),
    },
  ];

  const renderDesglose = (record) => {
    const tieneDetalle = (record.detalle?.length ?? 0) > 0;
    const tieneFormaPago = (record.desglose?.length ?? 0) > 0;

    if (!tieneDetalle && !tieneFormaPago) {
      return (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Sin desglose disponible
        </Typography.Text>
      );
    }

    const renderFilas = (items, getLabel, getKey) =>
      items.map((d) => (
        <div
          key={getKey(d)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            padding: '3px 0',
            borderBottom: '1px dashed #3a3a3a',
          }}
        >
          <span style={{ color: '#b0b0b0' }}>{getLabel(d)}</span>
          <span style={{ color: '#e8e8e8', whiteSpace: 'nowrap' }}>{formatMoneda(d.monto)}</span>
        </div>
      ));

    return (
      <div style={{ paddingLeft: 24, maxWidth: 480 }}>
        {tieneDetalle && (
          <>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
            >
              {record.detalle_titulo || 'Detalle'}
            </Typography.Text>
            {renderFilas(
              record.detalle,
              (d) => {
                const cant =
                  d.cantidad != null && Number(d.cantidad) > 0
                    ? ` (x${Number(d.cantidad)})`
                    : '';
                const extra = d.extra ? ` · ${d.extra}` : '';
                return `${d.label}${cant}${extra}`;
              },
              (d) => `det-${d.label}-${d.monto}`
            )}
          </>
        )}
        {tieneDetalle && tieneFormaPago && (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 11, display: 'block', margin: '10px 0 4px' }}
          >
            Por forma de pago
          </Typography.Text>
        )}
        {tieneFormaPago &&
          !tieneDetalle &&
          renderFilas(
            record.desglose,
            (d) => d.forma_pago,
            (d) => `fp-${d.forma_pago}`
          )}
        {tieneFormaPago &&
          tieneDetalle &&
          renderFilas(
            record.desglose,
            (d) => d.forma_pago,
            (d) => `fp-${d.forma_pago}`
          )}
      </div>
    );
  };

  const columnsFormaPago = (color) => [
    {
      title: 'Forma de pago',
      dataIndex: 'forma_pago',
      key: 'forma_pago',
      render: (fp) => <span style={{ color: '#e8e8e8' }}>{fp}</span>,
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      align: 'right',
      width: 160,
      render: (monto) => (
        <span style={{ color, fontWeight: 500 }}>{formatMoneda(monto)}</span>
      ),
    },
  ];

  const renderTotalRow = (label, value, color) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 16px',
        marginBottom: 16,
        background: '#1f1f1f',
        borderRadius: 6,
      }}
    >
      <span style={{ color: '#b0b0b0', fontWeight: 500 }}>{label}</span>
      <span style={{ color, fontWeight: 600, fontSize: 15 }}>{formatMoneda(value)}</span>
    </div>
  );

  const renderPorFormaPago = (tabla) => {
    const ingresos = tabla?.por_forma_pago?.ingresos || [];
    const egresos = tabla?.por_forma_pago?.egresos || [];
    if (!ingresos.length && !egresos.length) return null;

    return (
      <Card
        size="small"
        title={<span style={{ color: '#ffffff' }}>Totales por forma de pago</span>}
        style={{
          marginTop: 16,
          background: '#262626',
          borderColor: '#404040',
        }}
        styles={{ header: { borderBottom: '1px solid #404040' } }}
      >
        {ingresos.length > 0 && (
          <>
            <Typography.Text strong style={{ color: '#52c41a' }}>
              Ingresos
            </Typography.Text>
            <Table
              style={{ marginTop: 8, marginBottom: 12 }}
              dataSource={ingresos}
              columns={columnsFormaPago('#52c41a')}
              rowKey="forma_pago"
              size="small"
              pagination={false}
            />
          </>
        )}
        {egresos.length > 0 && (
          <>
            <Typography.Text strong style={{ color: '#ff4d4f' }}>
              Egresos
            </Typography.Text>
            <Table
              style={{ marginTop: 8 }}
              dataSource={egresos}
              columns={columnsFormaPago('#ff4d4f')}
              rowKey="forma_pago"
              size="small"
              pagination={false}
            />
          </>
        )}
      </Card>
    );
  };

  const renderTablaFinanciera = (tabla, titulo, subtitulo) => {
    const data = tabla || {
      ingresos: [],
      egresos: [],
      total_ingresos: 0,
      total_egresos: 0,
      total_real: 0,
    };
    return (
      <Card
        title={
          <div>
            <div style={{ color: '#ffffff' }}>{titulo}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              {subtitulo}
            </Typography.Text>
          </div>
        }
        style={{ background: '#2d2d2d', borderColor: '#404040', height: '100%' }}
      >
        <Typography.Text strong style={{ color: '#52c41a' }}>
          Ingresos
        </Typography.Text>
        <Table
          style={{ marginTop: 8, marginBottom: 12 }}
          dataSource={data.ingresos}
          columns={columnsMovimiento('#52c41a')}
          rowKey="key"
          size="small"
          pagination={false}
          expandable={{
            expandedRowRender: renderDesglose,
            rowExpandable: (r) =>
              (r.detalle?.length ?? 0) > 0 || (r.desglose?.length ?? 0) > 0,
          }}
        />
        {renderTotalRow('Total ingresos', data.total_ingresos, '#52c41a')}

        <Typography.Text strong style={{ color: '#ff4d4f' }}>
          Egresos
        </Typography.Text>
        <Table
          style={{ marginTop: 8, marginBottom: 12 }}
          dataSource={data.egresos}
          columns={columnsMovimiento('#ff4d4f')}
          rowKey="key"
          size="small"
          pagination={false}
          expandable={{
            expandedRowRender: renderDesglose,
            rowExpandable: (r) =>
              (r.detalle?.length ?? 0) > 0 || (r.desglose?.length ?? 0) > 0,
          }}
        />
        {renderTotalRow('Total egresos', data.total_egresos, '#ff4d4f')}

        <Divider style={{ borderColor: '#404040', margin: '4px 0 16px' }} />
        <Statistic
          title="Total real (ingresos - egresos)"
          value={data.total_real}
          valueStyle={{ color: data.total_real >= 0 ? '#52c41a' : '#ff4d4f', fontSize: 24 }}
          formatter={(value) => formatMoneda(value)}
        />

        {renderPorFormaPago(data)}
      </Card>
    );
  };

  const renderResumenFinanciero = () => (
    <div style={{ marginBottom: 24 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Facturado vs. Caja real"
        description="Facturado = todo lo vendido en el período (por fecha de venta), aunque no se haya cobrado. Caja real = lo que ingresó efectivamente (por fecha de cobro). La diferencia son las ventas que quedaron en cuenta corriente. Tocá cada fila para ver el desglose por forma de pago. Abajo de cada tabla está el total consolidado por forma de pago."
      />
      <Spin spinning={loadingFinanciero}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            {renderTablaFinanciera(
              resumenFinanciero?.facturado,
              'Facturado (ventas del período)',
              'Todo lo vendido, por fecha de venta'
            )}
          </Col>
          <Col xs={24} lg={12}>
            {renderTablaFinanciera(
              resumenFinanciero?.caja,
              'Caja real (ingresado en el período)',
              'Lo que entró efectivamente, por fecha de cobro'
            )}
          </Col>
        </Row>
      </Spin>
    </div>
  );

  if (loading && !dashboardData) {
    return (
      <MainLayout>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ color: '#ffffff', marginBottom: 16 }}>
            Dashboard de Gestión
          </Title>
          
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              value={periodo}
              onChange={(value) => {
                setPeriodo(value);
                if (value !== 'personalizado') {
                  setFechaDesde(null);
                  setFechaHasta(null);
                }
              }}
              style={{ width: 200 }}
            >
              <Select.Option value="hoy">Hoy</Select.Option>
              <Select.Option value="ayer">Ayer</Select.Option>
              <Select.Option value="ultimos7">Últimos 7 días</Select.Option>
              <Select.Option value="ultimos30">Últimos 30 días</Select.Option>
              <Select.Option value="mesActual">Mes actual</Select.Option>
              <Select.Option value="mesAnterior">Mes anterior</Select.Option>
              <Select.Option value="anoActual">Año actual</Select.Option>
              <Select.Option value="personalizado">Rango personalizado</Select.Option>
            </Select>
            
            {periodo === 'personalizado' && (
              <RangePicker
                value={fechaDesde && fechaHasta ? [fechaDesde, fechaHasta] : null}
                onChange={(dates) => {
                  if (dates) {
                    setFechaDesde(dates[0]);
                    setFechaHasta(dates[1]);
                  } else {
                    setFechaDesde(null);
                    setFechaHasta(null);
                  }
                }}
                format="DD/MM/YYYY"
              />
            )}

            {hasPermission('usuarios', 'ver') && listaUsuarios.length > 0 && (
              <Select
                allowClear
                placeholder="Todos los usuarios"
                style={{ minWidth: isMobile ? '100%' : 220 }}
                value={usuarioFiltroId}
                onChange={(v) => setUsuarioFiltroId(v ?? null)}
                options={listaUsuarios.map((u) => ({
                  value: u.usu_id,
                  label: u.usu_usuario,
                }))}
                showSearch
                optionFilterProp="label"
              />
            )}
            
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadDashboardData();
                loadResumenCajaBar();
                loadResumenFinanciero();
              }}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        </div>

        {/* Alertas */}
        {dashboardData?.alertas?.stockBajo?.length > 0 && (
          <Alert
            message={`${dashboardData.alertas.stockBajo.length} artículo(s) con stock bajo`}
            type="warning"
            icon={<WarningOutlined />}
            style={{ marginBottom: 24 }}
            showIcon
          />
        )}

        <Tabs
          defaultActiveKey="resumen"
          items={[
            {
              key: 'resumen',
              label: 'Resumen financiero',
              children: (
                <>
                  {renderResumenFinanciero()}

        {/* Ingresos Mercado Pago (reservas club con preference MP) */}
        <Card style={{ marginBottom: 24, backgroundColor: '#2d2d2d', borderColor: '#404040' }}>
          <Spin spinning={loadingResumenCaja}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                flexWrap: 'wrap',
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/mpago.png`}
                alt="Mercado Pago"
                style={{ height: 96, width: 'auto', maxWidth: 'min(100%, 320px)', objectFit: 'contain' }}
              />
              <Statistic
                title="Ingresos Mercado Pago"
                value={resumenCajaBar?.ingresos_mercado_pago_reservas ?? 0}
                valueStyle={{ color: '#52c41a' }}
                formatter={(value) =>
                  `$${Number(value).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                }
              />
            </div>
          </Spin>
        </Card>

        {/* Cobros en cancha: alquiler de turnos vs consumos */}
        <Card
          title="Cobros en cancha"
          style={{ marginBottom: 24, backgroundColor: '#2d2d2d', borderColor: '#404040' }}
        >
          <Spin spinning={loadingResumenCaja}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 20,
                flexWrap: 'wrap',
              }}
            >
              <ClockCircleOutlined
                style={{ fontSize: 56, color: '#1890ff', flexShrink: 0, marginTop: 4 }}
              />
              <div style={{ flex: 1, minWidth: 260 }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <Statistic
                      title="Turnos vendidos (alquiler)"
                      value={resumenCajaBar?.turnos_cobrados?.vendido_alquiler ?? 0}
                      valueStyle={{ color: '#52c41a', fontSize: 20 }}
                      formatter={(value) => formatMonedaDecimal(value)}
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {Math.round(resumenCajaBar?.turnos_cobrados?.cantidad_turnos_vendidos ?? 0)} turnos · por fecha de venta
                    </Typography.Text>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Statistic
                      title="Turnos cobrados (alquiler)"
                      value={resumenCajaBar?.turnos_cobrados?.cobrado_alquiler ?? 0}
                      valueStyle={{ color: '#73d13d', fontSize: 20 }}
                      formatter={(value) => formatMonedaDecimal(value)}
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Solo alquiler de cancha · por fecha de cobro
                    </Typography.Text>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Statistic
                      title="Consumos cobrados en cancha"
                      value={resumenCajaBar?.turnos_cobrados?.cobrado_consumos_cancha ?? 0}
                      valueStyle={{ color: '#faad14', fontSize: 20 }}
                      formatter={(value) => formatMonedaDecimal(value)}
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Bebidas, comida, etc. · por fecha de cobro
                    </Typography.Text>
                  </Col>
                </Row>
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #404040' }}>
                  <Statistic
                    title="Total cobrado en cancha (turno + consumos)"
                    value={resumenCajaBar?.turnos_cobrados?.total_cobrado ?? 0}
                    valueStyle={{ color: '#e8e8e8', fontSize: 18 }}
                    formatter={(value) => formatMonedaDecimal(value)}
                  />
                </div>
                {(resumenCajaBar?.turnos_cobrados?.por_forma_pago?.length ?? 0) > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Total cobrado en cancha por forma de pago
                    </Typography.Text>
                    <Space size={[8, 8]} wrap>
                      {resumenCajaBar.turnos_cobrados.por_forma_pago.map((fp) => (
                        <Tag
                          key={fp.forma_pago}
                          color="blue"
                          style={{ margin: 0, padding: '4px 10px', fontSize: 13 }}
                        >
                          {fp.forma_pago}: {formatMonedaDecimal(fp.monto_total)}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
              </div>
            </div>
          </Spin>
        </Card>

        {/* Resumen estilo Caja Diaria: Ingresos, Egreso Compras, Egreso Gastos, Cuenta Corriente */}
        <Card style={{ marginBottom: 24, backgroundColor: '#2d2d2d', borderColor: '#404040' }}>
          <Spin spinning={loadingResumenCaja}>
            <Row
              gutter={16}
              wrap={isMobile}
              style={!isMobile ? { flexWrap: 'nowrap', overflowX: 'auto' } : undefined}
            >
              <Col
                xs={24}
                sm={12}
                md={6}
                lg={6}
                flex={isMobile ? undefined : '1 1 0'}
                style={!isMobile ? { minWidth: 140 } : undefined}
              >
                <Statistic
                  title="Ingresos"
                  value={resumenCajaBar?.total_ingresos ?? 0}
                  prefix="$"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col
                xs={24}
                sm={12}
                md={6}
                lg={6}
                flex={isMobile ? undefined : '1 1 0'}
                style={!isMobile ? { minWidth: 140 } : undefined}
              >
                <Statistic
                  title="Egreso Compras"
                  value={resumenCajaBar?.total_egresos_compras ?? 0}
                  prefix="$"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
              <Col
                xs={24}
                sm={12}
                md={6}
                lg={6}
                flex={isMobile ? undefined : '1 1 0'}
                style={!isMobile ? { minWidth: 140 } : undefined}
              >
                <Statistic
                  title="Egreso Gastos"
                  value={resumenCajaBar?.total_egresos_gastos ?? 0}
                  prefix="$"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
              <Col
                xs={24}
                sm={12}
                md={6}
                lg={6}
                flex={isMobile ? undefined : '1 1 0'}
                style={!isMobile ? { minWidth: 140 } : undefined}
              >
                <Statistic
                  title="Cuenta Corriente"
                  value={resumenCajaBar?.cuenta_corriente ?? 0}
                  prefix="$"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>
          </Spin>
        </Card>

                </>
              ),
            },
            {
              key: 'actividad',
              label: 'Actividad y ventas',
              children: (
                <>
        {/* KPIs secundarios */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={12}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Valor Inventario"
                value={stats.stock?.valorTotal || 0}
                prefix={<DatabaseOutlined />}
                valueStyle={{ color: '#1890ff' }}
                formatter={(value) => formatMoneda(value)}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={12}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Stock Bajo"
                value={stats.stock?.stockBajoCount || 0}
                prefix={<WarningOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Gráficos principales */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Ingresos vs Egresos"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={ingresosVsEgresosData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis dataKey="fecha" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} />
                  <YAxis stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8, color: '#ffffff' }}
                    labelStyle={{ color: '#e8e8e8' }}
                    itemStyle={{ color: '#ffffff' }}
                    formatter={(value) => formatMoneda(value)}
                  />
                  <Legend wrapperStyle={{ color: '#b0b0b0' }} />
                  <Area type="monotone" dataKey="ingresos" stackId="1" stroke="#52c41a" fill="#52c41a" fillOpacity={0.6} />
                  <Area type="monotone" dataKey="egresos" stackId="2" stroke="#f5222d" fill="#f5222d" fillOpacity={0.6} />
                  <Line type="monotone" dataKey="ganancia" stroke="#1890ff" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card
              title="Ingresos por Forma de Pago"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={ingresosPorFormaPagoData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {ingresosPorFormaPagoData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const item = payload[0];
                      const nombre = item.payload?.name || item.name;

                      return (
                        <div style={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8, padding: '10px 14px' }}>
                          <span style={{ color: '#ffffff' }}>
                            {nombre}: {formatMoneda(item.value)}
                          </span>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Gráfico de gastos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Gastos por artículo"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              {gastosPorPeriodoData.length === 0 ? (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>
                  No hay gastos en el período seleccionado
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(300, gastosPorPeriodoData.length * 32)}>
                  <BarChart data={gastosPorPeriodoData} layout="vertical" margin={{ left: 8, right: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                    <XAxis type="number" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                    <YAxis dataKey="nombre" type="category" width={180} stroke="#8c8c8c" tick={{ fill: '#b0b0b0', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                      labelStyle={{ color: '#e8e8e8' }}
                      formatter={(value) => formatMoneda(value)}
                      labelFormatter={(label, payload) => (payload?.[0]?.payload ? `${payload[0].payload.nombre} (cant: ${payload[0].payload.cantidad})` : label)}
                    />
                    <Bar dataKey="total" name="Gastos" fill="#f5222d" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
        </Row>

        {/* Gráficos de barras */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Top 10 Productos Más Vendidos"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData?.top?.productos?.slice(0, 10) || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis type="number" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                  <YAxis dataKey="nombre" type="category" width={150} stroke="#8c8c8c" tick={{ fill: '#b0b0b0', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                    labelStyle={{ color: '#e8e8e8' }}
                    formatter={(value) => formatMoneda(value)}
                  />
                  <Bar dataKey="monto_total" fill="#52c41a" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card
              title="Ventas por Categoría"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ventasPorCategoriaData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis dataKey="categoria" stroke="#8c8c8c" tick={{ fill: '#b0b0b0', fontSize: 12 }} angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                    labelStyle={{ color: '#e8e8e8' }}
                    formatter={(value) => formatMoneda(value)}
                  />
                  <Bar dataKey="monto" fill="#1890ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Tablas */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Top 10 Productos"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <Table
                dataSource={dashboardData?.top?.productos || []}
                columns={columnsTopProductos}
                pagination={false}
                size="small"
                rowKey="articulo_id"
              />
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card
              title="Top 10 Proveedores"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <Table
                dataSource={dashboardData?.top?.proveedores || []}
                columns={columnsTopProveedores}
                pagination={false}
                size="small"
                rowKey="proveedor_id"
              />
            </Card>
          </Col>
        </Row>

                </>
              ),
            },
            {
              key: 'detalle',
              label: 'Alertas y últimos movimientos',
              children: (
                <>
        {/* Alertas y recientes */}
        <Row gutter={[16, 16]}>
          {dashboardData?.alertas?.stockBajo?.length > 0 && (
            <Col xs={24} lg={12}>
              <Card
                title={<><WarningOutlined style={{ color: '#faad14' }} /> Artículos con Stock Bajo</>}
                style={{ background: '#2d2d2d', borderColor: '#404040' }}
              >
                <Table
                  dataSource={dashboardData.alertas.stockBajo}
                  columns={columnsStockBajo}
                  pagination={false}
                  size="small"
                  rowKey="articulo_id"
                />
              </Card>
            </Col>
          )}
          
          <Col xs={24} lg={dashboardData?.alertas?.stockBajo?.length > 0 ? 12 : 24}>
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Card
                  title="Últimas Ventas"
                  style={{ background: '#2d2d2d', borderColor: '#404040' }}
                >
                  <Table
                    dataSource={dashboardData?.recientes?.ventas || []}
                    columns={columnsUltimasVentas}
                    pagination={false}
                    size="small"
                    rowKey="venta_id"
                  />
                </Card>
              </Col>
              
              <Col xs={24}>
                <Card
                  title="Últimas Compras"
                  style={{ background: '#2d2d2d', borderColor: '#404040' }}
                >
                  <Table
                    dataSource={dashboardData?.recientes?.compras || []}
                    columns={columnsUltimasCompras}
                    pagination={false}
                    size="small"
                    rowKey="compra_id"
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
                </>
              ),
            },
          ]}
        />
      </div>
    </MainLayout>
  );
};

export default DashboardGestion;
